import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/roles";
import { PARTNER_CATEGORIES, catColor } from "@/lib/partners";
import { toast } from "sonner";
import {
  Building2, Plus, X, Phone, Mail, Users, MapPin, StickyNote, CalendarClock,
  AlertTriangle, Clock, CheckCircle2, ChevronRight, Save,
} from "lucide-react";

const TOUCH_TYPES = [
  { key: "call", label: "Call", icon: Phone },
  { key: "email", label: "Email", icon: Mail },
  { key: "meeting", label: "Meeting", icon: Users },
  { key: "visit", label: "Visit", icon: MapPin },
  { key: "note", label: "Note", icon: StickyNote },
];
const TOUCH_LABEL = Object.fromEntries(TOUCH_TYPES.map((t) => [t.key, t.label]));

const STATUS_META = {
  overdue: { label: "Overdue", cls: "bg-[#b91c1c]/10 text-[#b91c1c] border-[#b91c1c]/30", icon: AlertTriangle },
  due_today: { label: "Due today", cls: "bg-[#FBBF24]/20 text-[#7a5a1a] border-[#FBBF24]/50", icon: Clock },
  due_soon: { label: "Due soon", cls: "bg-[#FFF9C4] text-[#7a5a1a] border-[#FFCB05]/40", icon: Clock },
  upcoming: { label: "Upcoming", cls: "bg-[#1E73C9]/10 text-[#1E73C9] border-[#1E73C9]/20", icon: CalendarClock },
  none: { label: "No date set", cls: "bg-[#15233B]/5 text-[#15233B]/50 border-[#15233B]/10", icon: CalendarClock },
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString(); }
  catch { return d; }
};

const inputCls = "w-full border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30";
const labelCls = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60";

function StatusBadge({ status, next, days }) {
  const m = STATUS_META[status] || STATUS_META.none;
  const Icon = m.icon;
  let sub = "";
  if (status === "overdue") sub = `${Math.abs(days)}d late`;
  else if (status === "due_soon" || status === "upcoming") sub = `in ${days}d`;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${m.cls}`} data-testid="followup-badge">
      <Icon className="h-3 w-3" />
      {m.label}{next && status !== "none" ? ` · ${fmtDate(next)}` : ""}{sub ? ` (${sub})` : ""}
    </span>
  );
}

function AddOrgDialog({ users, defaultOwnerId, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", category: "Community Org", contact_person: "", phone: "", email: "",
    owner_id: defaultOwnerId || "", next_followup_date: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) { setErr("Name must be at least 2 characters"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/referral-partners", {
        name: f.name.trim(), category: f.category, contact_person: f.contact_person.trim(),
        phone: f.phone.trim(), email: f.email.trim(), notes: f.notes.trim(),
        outreach: true, owner_id: f.owner_id || null,
        next_followup_date: f.next_followup_date || null,
      });
      toast.success(`${data.name} added to outreach`);
      onCreated?.(data);
      onClose();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#15233B]/50 flex items-center justify-center p-4" onClick={onClose}>
      <form data-testid="outreach-add-dialog" onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#15233B]/10 flex items-center justify-between bg-[#F6F3EC]">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#1E73C9]" />
            <h2 className="text-base font-extrabold text-[#15233B]">Add organization</h2>
          </div>
          <button type="button" data-testid="outreach-add-close" onClick={onClose} className="p-1.5 rounded hover:bg-[#15233B]/5"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className={labelCls}>Organization name</label>
            <input data-testid="outreach-name" autoFocus value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g., United Community Center" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <select data-testid="outreach-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                {PARTNER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Relationship owner</label>
              <select data-testid="outreach-owner" value={f.owner_id} onChange={(e) => set("owner_id", e.target.value)} className={inputCls}>
                <option value="">— unassigned —</option>
                {users.filter((u) => u.role !== "field_agent").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Contact person</label>
            <input data-testid="outreach-contact" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Name / title" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="outreach-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(414) 555-0100" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input data-testid="outreach-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="name@org.org" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Next follow-up date (optional)</label>
            <input data-testid="outreach-next-date" type="date" value={f.next_followup_date} onChange={(e) => set("next_followup_date", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea data-testid="outreach-notes" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
          </div>
          {err && <div data-testid="outreach-add-error" className="text-xs text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded px-2.5 py-2">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-[#15233B]/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
          <button type="submit" data-testid="outreach-add-submit" disabled={busy} className="px-4 py-2 rounded bg-[#1E73C9] text-white text-sm font-bold hover:bg-[#15233B] disabled:opacity-60">
            {busy ? "Adding…" : "Add organization"}
          </button>
        </div>
      </form>
    </div>
  );
}

function OrgDrawer({ org, users, canEdit, onClose, onChanged }) {
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [touchType, setTouchType] = useState("call");
  const [touchNote, setTouchNote] = useState("");
  const [touchOutcome, setTouchOutcome] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextNote, setNextNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({ category: org.category, owner_id: org.owner_id || "", contact_person: org.contact_person || "", phone: org.phone || "", email: org.email || "" });
  const [editDirty, setEditDirty] = useState(false);

  const loadLog = useCallback(() => {
    setLoadingLog(true);
    api.get(`/outreach/${org.id}/log`).then(({ data }) => setLog(data || [])).catch(() => setLog([])).finally(() => setLoadingLog(false));
  }, [org.id]);
  useEffect(() => { loadLog(); }, [loadLog]);

  const setE = (k, v) => { setEdit((x) => ({ ...x, [k]: v })); setEditDirty(true); };

  const logTouch = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/outreach/${org.id}/log`, {
        type: touchType, note: touchNote.trim(), outcome: touchOutcome.trim() || null,
        ...(nextDate ? { next_followup_date: nextDate, next_followup_note: nextNote.trim() } : {}),
      });
      toast.success("Follow-up logged");
      setTouchNote(""); setTouchOutcome(""); setNextDate(""); setNextNote("");
      loadLog();
      onChanged?.(data.partner);
      window.dispatchEvent(new Event("avida-outreach-updated"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/referral-partners/${org.id}`, {
        category: edit.category, owner_id: edit.owner_id || null,
        contact_person: edit.contact_person, phone: edit.phone, email: edit.email,
      });
      toast.success("Saved");
      setEditDirty(false);
      onChanged?.({ ...org, ...data });
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const clearFollowup = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/referral-partners/${org.id}`, { next_followup_date: null, next_followup_note: "" });
      toast.success("Follow-up cleared");
      onChanged?.({ ...org, ...data, followup_status: "none", next_followup_date: null });
      window.dispatchEvent(new Event("avida-outreach-updated"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#15233B]/40" onClick={onClose}>
      <div data-testid="outreach-drawer" onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#F6F3EC] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-[#15233B] text-white px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFCB05]/90">Partner outreach</div>
            <h2 className="text-lg font-extrabold leading-tight" data-testid="outreach-drawer-name">{org.name}</h2>
            <div className="mt-1"><StatusBadge status={org.followup_status} next={org.next_followup_date} days={org.days_until} /></div>
          </div>
          <button data-testid="outreach-drawer-close" onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Details */}
          <section className="bg-white rounded-lg border border-[#15233B]/10 p-4 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#15233B]/60">Organization details</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Category</label>
                <select data-testid="drawer-category" disabled={!canEdit} value={edit.category} onChange={(e) => setE("category", e.target.value)} className={inputCls}>
                  {PARTNER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Owner</label>
                <select data-testid="drawer-owner" disabled={!canEdit} value={edit.owner_id} onChange={(e) => setE("owner_id", e.target.value)} className={inputCls}>
                  <option value="">— unassigned —</option>
                  {users.filter((u) => u.role !== "field_agent").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Contact person</label>
              <input data-testid="drawer-contact" disabled={!canEdit} value={edit.contact_person} onChange={(e) => setE("contact_person", e.target.value)} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Phone</label>
                <input data-testid="drawer-phone" disabled={!canEdit} value={edit.phone} onChange={(e) => setE("phone", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="drawer-email" disabled={!canEdit} value={edit.email} onChange={(e) => setE("email", e.target.value)} className={inputCls} />
              </div>
            </div>
            {canEdit && editDirty && (
              <button data-testid="drawer-save-details" onClick={saveDetails} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#15233B] text-white text-xs font-bold hover:bg-[#1E73C9] disabled:opacity-60">
                <Save className="h-3.5 w-3.5" /> Save details
              </button>
            )}
            <div className="text-[11px] text-[#15233B]/55 pt-1">
              Last contacted: {org.last_contacted_at ? fmtDate(org.last_contacted_at) : "never"}
            </div>
          </section>

          {/* Log a follow-up */}
          <section className="bg-white rounded-lg border border-[#15233B]/10 p-4 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#15233B]/60">Log a follow-up</div>
            <div className="flex flex-wrap gap-1.5">
              {TOUCH_TYPES.map((t) => {
                const Icon = t.icon;
                const active = touchType === t.key;
                return (
                  <button key={t.key} data-testid={`drawer-touch-${t.key}`} onClick={() => setTouchType(t.key)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${active ? "bg-[#1E73C9] text-white border-[#1E73C9]" : "bg-white text-[#15233B]/70 border-[#15233B]/15 hover:border-[#1E73C9]/40"}`}>
                    <Icon className="h-3.5 w-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>
            <input data-testid="drawer-touch-outcome" value={touchOutcome} onChange={(e) => setTouchOutcome(e.target.value)} placeholder="Outcome (optional) — e.g., Left voicemail" className={inputCls} />
            <textarea data-testid="drawer-touch-note-input" rows={2} value={touchNote} onChange={(e) => setTouchNote(e.target.value)} placeholder="Notes about this touch (no PHI)…" className={inputCls} />
            <div className="rounded-md bg-[#FFF9C4]/40 border border-[#FFCB05]/30 p-2.5 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7a5a1a]">Set next follow-up (optional)</div>
              <input data-testid="drawer-next-date" type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className={inputCls} />
              {nextDate && <input data-testid="drawer-next-note" value={nextNote} onChange={(e) => setNextNote(e.target.value)} placeholder="What's the next step?" className={inputCls} />}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="drawer-log-submit" onClick={logTouch} disabled={saving} className="px-4 py-2 rounded bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] disabled:opacity-60">
                {saving ? "Saving…" : "Log follow-up"}
              </button>
              {org.next_followup_date && (
                <button data-testid="drawer-clear-followup" onClick={clearFollowup} disabled={saving} className="text-xs font-semibold text-[#15233B]/55 hover:text-[#b91c1c]">Clear follow-up date</button>
              )}
            </div>
          </section>

          {/* History */}
          <section className="bg-white rounded-lg border border-[#15233B]/10 p-4">
            <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#15233B]/60 mb-3">Touch history</div>
            {loadingLog ? (
              <div className="text-xs text-[#15233B]/50 py-3">Loading…</div>
            ) : log.length === 0 ? (
              <div data-testid="outreach-log-empty" className="text-xs italic text-[#15233B]/40 py-3">No follow-ups logged yet.</div>
            ) : (
              <ul data-testid="outreach-log-list" className="space-y-3">
                {log.map((l) => (
                  <li key={l.id} className="flex gap-2.5">
                    <div className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#1E73C9]/10 grid place-items-center">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#1E73C9]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-[#15233B]">
                        {TOUCH_LABEL[l.type] || l.type}{l.outcome ? ` · ${l.outcome}` : ""}
                      </div>
                      {l.note && <div className="text-xs text-[#15233B]/75 whitespace-pre-wrap">{l.note}</div>}
                      <div className="text-[10px] text-[#15233B]/45 mt-0.5">{l.by_user_name} · {new Date(l.created_at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default function OutreachPage() {
  const { user } = useAuth();
  const canEdit = isManager(user);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(() => {
    api.get("/outreach").then(({ data }) => { setItems(data.items || []); setCounts(data.counts || {}); })
      .catch(() => {}).finally(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => setUsers([]));
  }, []);

  const filtered = useMemo(() => items.filter((p) =>
    (!catFilter || p.category === catFilter) && (!statusFilter || p.followup_status === statusFilter)
  ), [items, catFilter, statusFilter]);

  const chips = [
    { key: "", label: "All", val: counts.total || 0, cls: "bg-[#15233B] text-white" },
    { key: "overdue", label: "Overdue", val: counts.overdue || 0, cls: "bg-[#b91c1c]/10 text-[#b91c1c] border border-[#b91c1c]/30" },
    { key: "due_today", label: "Due today", val: counts.due_today || 0, cls: "bg-[#FBBF24]/20 text-[#7a5a1a] border border-[#FBBF24]/50" },
    { key: "due_soon", label: "Due soon", val: counts.due_soon || 0, cls: "bg-[#FFF9C4] text-[#7a5a1a] border border-[#FFCB05]/40" },
    { key: "none", label: "No date", val: counts.none || 0, cls: "bg-white text-[#15233B]/60 border border-[#15233B]/15" },
  ];

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl w-full mx-auto space-y-6 flex-1">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[#1E73C9]"><Building2 className="h-5 w-5" /><span className="text-[10px] uppercase tracking-[0.25em] font-bold">Relationship building</span></div>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Partner Outreach</h1>
            <p className="text-sm text-[#15233B]/60">Organizations you're cultivating for referrals — log touches and never miss a follow-up.</p>
          </div>
          {canEdit && (
            <button data-testid="outreach-add-button" onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D]">
              <Plus className="h-4 w-4" /> Add organization
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2" data-testid="outreach-filter-chips">
          {chips.map((c) => (
            <button key={c.key || "all"} data-testid={`outreach-chip-${c.key || "all"}`} onClick={() => setStatusFilter(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${c.cls} ${statusFilter === c.key ? "ring-2 ring-[#1E73C9]/50 ring-offset-1" : "opacity-90 hover:opacity-100"}`}>
              {c.label} · {c.val}
            </button>
          ))}
          <select data-testid="outreach-cat-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="ml-auto border border-[#15233B]/15 rounded-full px-3 py-1.5 text-xs font-semibold bg-white">
            <option value="">All categories</option>
            {PARTNER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="bg-white border border-[#15233B]/10 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/55 bg-[#F6F3EC]">
                <tr>
                  <th className="text-left py-2.5 px-4">Organization</th>
                  <th className="text-left py-2.5 px-2 hidden sm:table-cell">Category</th>
                  <th className="text-left py-2.5 px-2 hidden md:table-cell">Owner</th>
                  <th className="text-left py-2.5 px-2">Next follow-up</th>
                  <th className="py-2.5 px-2"></th>
                </tr>
              </thead>
              <tbody data-testid="outreach-list">
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`outreach-row-${p.id}`} onClick={() => setSelected(p)}
                    className="border-t border-[#15233B]/8 hover:bg-[#FFF9C4]/30 cursor-pointer">
                    <td className="py-3 px-4">
                      <div className="font-bold text-[#15233B]">{p.name}</div>
                      {(p.contact_person || p.phone) && (
                        <div className="text-[11px] text-[#15233B]/55">{[p.contact_person, p.phone].filter(Boolean).join(" · ")}</div>
                      )}
                    </td>
                    <td className="py-3 px-2 hidden sm:table-cell">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${catColor(p.category)}`}>{p.category}</span>
                    </td>
                    <td className="py-3 px-2 hidden md:table-cell text-[#15233B]/70 text-xs">{p.owner_name || "—"}</td>
                    <td className="py-3 px-2"><StatusBadge status={p.followup_status} next={p.next_followup_date} days={p.days_until} /></td>
                    <td className="py-3 px-2 text-right">
                      <ChevronRight className="h-4 w-4 text-[#15233B]/30 inline" />
                    </td>
                  </tr>
                ))}
                {loaded && filtered.length === 0 && (
                  <tr><td colSpan={5} data-testid="outreach-empty" className="py-12 text-center">
                    <Building2 className="h-8 w-8 text-[#15233B]/20 mx-auto mb-2" />
                    <div className="text-sm font-semibold text-[#15233B]/60">{items.length === 0 ? "No outreach organizations yet." : "None match this filter."}</div>
                    {canEdit && items.length === 0 && <div className="text-xs text-[#15233B]/45 mt-1">Click “Add organization” to start tracking a referral relationship.</div>}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <BrandFooter />
      {showAdd && <AddOrgDialog users={users} defaultOwnerId={user?.id} onClose={() => setShowAdd(false)} onCreated={() => { load(); window.dispatchEvent(new Event("avida-outreach-updated")); }} />}
      {selected && <OrgDrawer org={selected} users={users} canEdit={canEdit} onClose={() => { setSelected(null); load(); }}
        onChanged={(p) => { setSelected((s) => ({ ...s, ...p })); setItems((xs) => xs.map((x) => x.id === p.id ? { ...x, ...p } : x)); }} />}
    </div>
  );
}
